import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';

const Clients: React.FC = () => {
  const [clients, setClients] = useState<any[]>([]);
  const [newClient, setNewClient] = useState({ name: '', eventDate: '', contractPrice: '', notes: '', status: 'Prospective', statusNotes: '' });
  const [venues, setVenues] = useState<any[]>([]);
  const [venueSearch, setVenueSearch] = useState('');
  const [selectedVenueId, setSelectedVenueId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [sortField, setSortField] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
    fetchVenues();
  }, []);

  const fetchClients = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found. Please log in again.');
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/Clients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const clientData = response.data;
      clientData.forEach((client: any) => {
        if (client.submittedPayments && typeof client.submittedPayments === 'string') {
          try {
            client.submittedPayments = JSON.parse(client.submittedPayments);
          } catch (parseError) {
            console.error('Failed to parse submittedPayments for client:', client.id, parseError);
            client.submittedPayments = [];
          }
        } else {
          client.submittedPayments = [];
        }
      });
      setClients(clientData);
    } catch (error: any) {
      console.error('Failed to fetch clients:', error);
      if (error.response && error.response.status === 401) {
        setError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        window.dispatchEvent(new Event('authChange'));
        navigate('/');
      } else {
        setError(`Error: ${error.message}. Please try again.`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVenues = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found.');
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/Venues`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVenues(response.data);
    } catch (error: any) {
      console.error('Failed to fetch venues:', error);
      setError('Failed to load venues.');
    }
  };

  const handleCreateClient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newClient.name || !newClient.eventDate || !newClient.contractPrice) {
      setError('Name, Event Date, and Contract Price are required.');
      return;
    }
    setError('');
    setSuccess('');
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found. Please log in again.');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/Clients`,
        {
          name: newClient.name,
          venueId: selectedVenueId,
          eventDate: new Date(newClient.eventDate),
          contractPrice: parseFloat(newClient.contractPrice),
          notes: newClient.notes,
          status: newClient.status,
          statusNotes: newClient.statusNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewClient({ name: '', eventDate: '', contractPrice: '', notes: '', status: 'Prospective', statusNotes: '' });
      setSelectedVenueId(null);
      setVenueSearch('');
      setSuccess('Client created successfully!');
      setShowModal(false);
      fetchClients();
    } catch (error: any) {
      console.error('Failed to create client:', error);
      if (error.response && error.response.status === 401) {
        setError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        window.dispatchEvent(new Event('authChange'));
        navigate('/');
      } else {
        setError(`Error: ${error.message}. Please try again.`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const deleteClient = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this client? This action cannot be undone.')) return;
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found. Please log in again.');
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/Clients/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClients(clients.filter(client => client.id !== id));
      setSuccess('Client deleted successfully!');
    } catch (error: any) {
      console.error('Failed to delete client:', error);
      if (error.response && error.response.status === 401) {
        setError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        window.dispatchEvent(new Event('authChange'));
        navigate('/');
      } else {
        setError(`Error: ${error.message}. Please try again.`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (clientId: number, newStatus: string, newStatusNotes: string) => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      const client = clients.find(c => c.id === clientId);
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/Clients/${clientId}`,
        { ...client, status: newStatus, statusNotes: newStatusNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setClients(clients.map(c => c.id === clientId ? { ...c, status: newStatus, statusNotes: newStatusNotes } : c));
      setSuccess('Status updated successfully!');
    } catch (error: any) {
      console.error('Failed to update status:', error);
      setError('Failed to update status. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (field: string) => {
    const isAsc = sortField === field && sortDirection === 'asc';
    setSortField(field);
    setSortDirection(isAsc ? 'desc' : 'asc');

    const sortedClients = [...clients].sort((a, b) => {
      let valueA = a[field];
      let valueB = b[field];

      if (field === 'eventDate') {
        valueA = new Date(a.eventDate).getTime();
        valueB = new Date(b.eventDate).getTime();
      } else if (field === 'contractPrice' || field === 'currentBalance') {
        valueA = parseFloat(valueA);
        valueB = parseFloat(valueB);
      } else if (field === 'formCount') {
        valueA = parseInt(valueA, 10);
        valueB = parseInt(valueB, 10);
      } else {
        valueA = valueA ? valueA.toString().toLowerCase() : '';
        valueB = valueB ? valueB.toString().toLowerCase() : '';
      }

      if (valueA < valueB) return isAsc ? 1 : -1;
      if (valueA > valueB) return isAsc ? -1 : 1;
      return 0;
    });

    setClients(sortedClients);
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <div className="container mt-5">
      <h2 className="mb-4">Clients</h2>
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}
      {isLoading && <div className="alert alert-info">Loading...</div>}

      {showModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Client</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} disabled={isLoading}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleCreateClient}>
                  <div className="mb-3">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className={`form-control ${!newClient.name && error ? 'is-invalid' : ''}`}
                      placeholder="Enter name"
                      value={newClient.name}
                      onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                      disabled={isLoading}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Venue</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search venues..."
                      value={venueSearch}
                      onChange={(e) => setVenueSearch(e.target.value)}
                      disabled={isLoading}
                    />
                    <select
                      className="form-control mt-2"
                      value={selectedVenueId || ''}
                      onChange={(e) => setSelectedVenueId(e.target.value ? parseInt(e.target.value) : null)}
                      disabled={isLoading}
                    >
                      <option value="">Select a venue (optional)</option>
                      {venues.filter(v => v.name.toLowerCase().includes(venueSearch.toLowerCase())).map(venue => (
                        <option key={venue.id} value={venue.id}>{venue.name} - {venue.address}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Event Date</label>
                    <input
                      type="date"
                      className={`form-control ${!newClient.eventDate && error ? 'is-invalid' : ''}`}
                      value={newClient.eventDate}
                      onChange={(e) => setNewClient({ ...newClient, eventDate: e.target.value })}
                      disabled={isLoading}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Contract Price</label>
                    <input
                      type="number"
                      className={`form-control ${!newClient.contractPrice && error ? 'is-invalid' : ''}`}
                      placeholder="Enter price"
                      value={newClient.contractPrice}
                      onChange={(e) => setNewClient({ ...newClient, contractPrice: e.target.value })}
                      disabled={isLoading}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <select
                      className="form-control"
                      value={newClient.status}
                      onChange={(e) => setNewClient({ ...newClient, status: e.target.value })}
                      disabled={isLoading}
                    >
                      <option value="Prospective">Prospective</option>
                      <option value="Contract Sent">Contract Sent</option>
                      <option value="Booked">Booked</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Status Notes</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter status notes (optional)"
                      value={newClient.statusNotes}
                      onChange={(e) => setNewClient({ ...newClient, statusNotes: e.target.value })}
                      disabled={isLoading}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Notes</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter notes (optional)"
                      value={newClient.notes}
                      onChange={(e) => setNewClient({ ...newClient, notes: e.target.value })}
                      disabled={isLoading}
                    />
                  </div>
                  <button type="submit" className="btn btn-primary w-100" disabled={isLoading}>
                    {isLoading ? 'Adding...' : 'Add Client'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card shadow-sm">
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Client List ({clients.length})</h4>
          <button
            className="btn btn-light text-dark"
            onClick={() => setShowModal(true)}
            disabled={isLoading}
          >
            Add Client
          </button>
        </div>
        <div className="card-body">
          {!isLoading && clients.length === 0 ? (
            <p className="text-muted text-center">No clients found.</p>
          ) : (
            <table className="table table-striped">
              <thead>
                <tr>
                  <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                    Name {getSortIcon('name')}
                  </th>
                  <th onClick={() => handleSort('venueName')} style={{ cursor: 'pointer' }}>
                    Venue {getSortIcon('venueName')}
                  </th>
                  <th onClick={() => handleSort('eventDate')} style={{ cursor: 'pointer' }}>
                    Event Date {getSortIcon('eventDate')}
                  </th>
                  <th onClick={() => handleSort('contractPrice')} style={{ cursor: 'pointer' }}>
                    Contract Price {getSortIcon('contractPrice')}
                  </th>
                  <th onClick={() => handleSort('currentBalance')} style={{ cursor: 'pointer' }}>
                    Balance {getSortIcon('currentBalance')}
                  </th>
                  <th onClick={() => handleSort('formCount')} style={{ cursor: 'pointer' }}>
                    Forms {getSortIcon('formCount')}
                  </th>
                  <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                    Status {getSortIcon('status')}
                  </th>
                  <th onClick={() => handleSort('notes')} style={{ cursor: 'pointer' }}>
                    Notes {getSortIcon('notes')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clients.map(client => (
                  <tr key={client.id}>
                    <td>{client.name}</td>
                    <td>{client.venueName || 'N/A'}</td>
                    <td>{new Date(client.eventDate).toLocaleDateString()}</td>
                    <td>${client.contractPrice.toFixed(2)}</td>
                    <td>${client.currentBalance.toFixed(2)}</td>
                    <td>{client.formCount}</td>
                    <td>
                      <select
                        className="form-select form-select-sm mb-1"
                        value={client.status}
                        onChange={(e) => handleStatusChange(client.id, e.target.value, client.statusNotes)}
                        disabled={isLoading}
                      >
                        <option value="Prospective">Prospective</option>
                        <option value="Contract Sent">Contract Sent</option>
                        <option value="Booked">Booked</option>
                      </select>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Status notes"
                        value={client.statusNotes || ''}
                        onChange={(e) => handleStatusChange(client.id, client.status, e.target.value)}
                        disabled={isLoading}
                      />
                    </td>
                    <td>{client.notes || 'N/A'}</td>
                    <td>
                      <Link to={`/clients/${client.id}`} className="btn btn-sm btn-outline-primary me-2">View</Link>
                      <button
                        onClick={() => deleteClient(client.id)}
                        className="btn btn-sm btn-danger"
                        disabled={isLoading}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Clients;